import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Loader2, Search, X, Check, Layers, Star, Link2, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${getApiUrl()}/api/master`;

// Relationship kinds, worded from the edited game's perspective.
const REL_KINDS = [
  { value: "alternate", label: "Alternate version of this game" },
  { value: "remaster_of_this", label: "Is a remaster of this game" },
  { value: "this_is_remaster", label: "This game is a remaster of…" },
  { value: "remake_of_this", label: "Is a remake of this game" },
  { value: "this_is_remake", label: "This game is a remake of…" },
  { value: "collection_containing_this", label: "Is a collection containing this game" },
  { value: "this_is_collection_of", label: "This game is a collection containing…" },
  { value: "sequel_of_this", label: "Is a sequel to this game" },
  { value: "this_is_sequel", label: "This game is a sequel to…" },
  { value: "prequel_of_this", label: "Is a prequel to this game" },
  { value: "this_is_prequel", label: "This game is a prequel to…" },
  { value: "spinoff_of_this", label: "Is a spin-off of this game" },
  { value: "this_is_spinoff", label: "This game is a spin-off of…" },
];
const KIND_SHORT = {
  alternate: "Alternate version",
  remaster_of_this: "Remaster of this",
  this_is_remaster: "This is a remaster of",
  remake_of_this: "Remake of this",
  this_is_remake: "This is a remake of",
  collection_containing_this: "Collection containing this",
  this_is_collection_of: "This collection contains",
  sequel_of_this: "Sequel to this",
  this_is_sequel: "This is a sequel to",
  prequel_of_this: "Prequel to this",
  this_is_prequel: "This is a prequel to",
  spinoff_of_this: "Spin-off of this",
  this_is_spinoff: "This is a spin-off of",
};

/**
 * MDB-editor dialog to link multiple VERSIONS of the same game together.
 * Separate from cross-play (multiplayer) links. One member is the "primary"
 * version — it supplies the collapsed Browse tile's art/name.
 */
export default function VersionLinksDialog({ open, onOpenChange, masterGameId, masterGameName, onSaved }) {
  const [game, setGame] = useState(null);
  const [linked, setLinked] = useState([]); // [{id, name, platforms, systems, boxArtUrl}]
  const [primaryId, setPrimaryId] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Related games (typed relationships)
  const [relations, setRelations] = useState([]);
  const [relKind, setRelKind] = useState("alternate");
  const [relSearchQ, setRelSearchQ] = useState("");
  const [relResults, setRelResults] = useState([]);
  const [relSearching, setRelSearching] = useState(false);
  const [relBusy, setRelBusy] = useState(false);
  const [relExpanded, setRelExpanded] = useState(null); // versionGroupId being expanded

  const loadRelations = useCallback(() => {
    if (!masterGameId) return;
    axios.get(`${API}/games/${masterGameId}/relations`)
      .then(r => setRelations(r.data.relations || []))
      .catch(() => setRelations([]));
  }, [masterGameId]);

  useEffect(() => {
    if (!open || !masterGameId) return;
    setLoading(true);
    setSearchQ("");
    setSearchResults([]);
    axios.get(`${API}/games/${masterGameId}/version-links`)
      .then(r => {
        setGame(r.data.game);
        setLinked(r.data.linked || []);
        setPrimaryId(r.data.primaryId || null);
      })
      .catch(err => toast.error(err.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
    loadRelations();
    setRelSearchQ("");
    setRelResults([]);
  }, [open, masterGameId, loadRelations]);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/version-search`, {
        params: { q, exclude_id: masterGameId, limit: 20 },
      });
      const linkedIds = new Set(linked.map(l => l.id));
      setSearchResults((r.data || []).filter(g => !linkedIds.has(g.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [masterGameId, linked]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  const addLink = (g) => {
    setLinked(prev => [...prev, g]);
    setSearchResults(prev => prev.filter(x => x.id !== g.id));
  };

  const removeLink = (id) => {
    setLinked(prev => prev.filter(g => g.id !== id));
    if (primaryId === id) setPrimaryId(null);
  };

  const runRelSearch = useCallback(async (q) => {
    if (!q.trim()) { setRelResults([]); return; }
    setRelSearching(true);
    try {
      const r = await axios.get(`${API}/version-search`, {
        params: { q, exclude_id: masterGameId, limit: 20, collapse_versions: true },
      });
      setRelResults(r.data || []);
    } catch {
      setRelResults([]);
    } finally {
      setRelSearching(false);
    }
  }, [masterGameId]);

  useEffect(() => {
    const t = setTimeout(() => runRelSearch(relSearchQ), 250);
    return () => clearTimeout(t);
  }, [relSearchQ, runRelSearch]);

  const addRelation = async (g) => {
    setRelBusy(true);
    try {
      await axios.post(`${API}/games/${masterGameId}/relations`, { other_id: g.id, kind: relKind });
      toast.success(`Added: ${KIND_SHORT[relKind]} — ${g.name}`);
      setRelSearchQ("");
      setRelResults([]);
      loadRelations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add relation");
    } finally {
      setRelBusy(false);
    }
  };

  const removeRelation = async (relationId) => {
    setRelBusy(true);
    try {
      await axios.delete(`${API}/game-relations/${relationId}`);
      loadRelations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove relation");
    } finally {
      setRelBusy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/games/${masterGameId}/version-links`, {
        linked_ids: linked.map(g => g.id),
        primary_id: primaryId,
      });
      toast.success(r.data?.message || "Version links saved");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // The full group (this game + linked) for the primary picker.
  const groupForPrimary = game ? [game, ...linked] : linked;

  const fmtMeta = (g) => {
    const plats = [...new Set((g.platforms || []).map(String))];
    const syss = (g.systems || []).map(String);
    const yr = g.releaseDate ? (String(g.releaseDate).match(/\d{4}/)?.[0] || null) : null;
    const parts = [];
    if (plats.length) parts.push(plats.join(" / "));
    if (syss.length) parts.push(syss.join(" / "));
    if (yr) parts.push(yr);
    return parts.join(" · ");
  };

  const GameRow = ({ g, action, canBePrimary }) => {
    const isPrimary = primaryId === g.id || (!primaryId && canBePrimary && g.id === masterGameId && linked.length === 0 && false);
    const meta = fmtMeta(g);
    return (
      <div className="flex items-start gap-2 p-2 rounded bg-white/[0.03] border border-white/5">
        {g.boxArtUrl && <img src={g.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm break-words leading-snug">{g.name}</div>
          <div className="text-white/40 text-xs break-words mt-0.5">
            {meta || "—"}
          </div>
        </div>
        {canBePrimary && (
          <button
            type="button"
            onClick={() => setPrimaryId(g.id)}
            data-testid={`vl-primary-${g.id}`}
            title={isPrimary ? "Primary version (shown on the collapsed tile)" : "Set as primary version"}
            className={`flex items-center gap-1 text-xs px-2 h-7 rounded border transition-colors ${
              isPrimary
                ? "bg-amber-500/20 border-amber-500/50 text-amber-300"
                : "bg-white/5 border-white/10 text-white/40 hover:text-amber-300 hover:border-amber-500/40"
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${isPrimary ? "fill-amber-300" : ""}`} />
            {isPrimary ? "Primary" : ""}
          </button>
        )}
        {action}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-cyan-400" />
            Linked &amp; Related Games{masterGameName ? <span className="text-white/40 font-normal text-base">— {masterGameName}</span> : null}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-white/40 text-xs">Link records that are different <span className="text-white/70">versions of the same game</span> (e.g. different editions or platform versions). When a user's "collapse linked versions" setting is on, the whole group shows as a single Browse tile using the <span className="text-amber-300">primary</span> version. This is separate from cross-play (multiplayer) links.</p>

            {/* This game — always part of the group; can be the primary */}
            {game && linked.length > 0 && (
              <div>
                <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">This game</p>
                <GameRow g={game} canBePrimary />
              </div>
            )}

            {/* Currently linked */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Linked versions ({linked.length})</p>
              {linked.length === 0 ? (
                <p className="text-white/30 text-xs italic">No linked versions yet. Search below to add some.</p>
              ) : (
                <div className="space-y-1.5">
                  {linked.map(g => (
                    <GameRow
                      key={g.id}
                      g={g}
                      canBePrimary
                      action={
                        <Button
                          data-testid={`vl-remove-${g.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLink(g.id)}
                          className="text-white/40 hover:text-red-400 h-7 px-2"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Add a version</p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="vl-search"
                  placeholder="Search MDB by name..."
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  className="pl-8 bg-white/5 border-white/10 text-white text-sm"
                />
                {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" />}
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1.5 mt-2 max-h-72 overflow-y-auto">
                  {searchResults.map(g => (
                    <GameRow
                      key={g.id}
                      g={g}
                      action={
                        <Button
                          data-testid={`vl-add-${g.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => addLink(g)}
                          className="text-emerald-400 hover:text-emerald-300 h-7 px-2"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                  ))}
                </div>
              )}
              {searchQ.trim() && !searching && searchResults.length === 0 && (
                <p className="text-white/30 text-xs italic mt-2">No more games match that search.</p>
              )}
            </div>

            {/* ===== Related games (typed relationships) ===== */}
            <div className="pt-2 border-t border-white/10">
              <p className="text-white/80 text-sm font-semibold mb-1 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-fuchsia-400" />
                Related games
              </p>
              <p className="text-white/40 text-xs mb-3">Link remakes, remasters, alternate versions, and collections. These do NOT collapse in Browse — they show as cross-references on the game's page.</p>

              {/* Current relations */}
              {relations.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {relations.map(r => (
                    <div key={r.relationId} className="flex items-start gap-2 p-2 rounded bg-white/[0.03] border border-white/5" data-testid={`rel-row-${r.relationId}`}>
                      {r.otherGame.boxArtUrl && <img src={r.otherGame.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm break-words leading-snug">{r.otherGame.name}</div>
                        {fmtMeta(r.otherGame) && (
                          <div className="text-white/40 text-xs break-words mt-0.5">{fmtMeta(r.otherGame)}</div>
                        )}
                        <div className="text-fuchsia-300/70 text-xs mt-0.5">
                          {KIND_SHORT[r.kind] || r.kind}
                          {r.otherGame.followsPrimaryName && (
                            <span className="text-white/40"> · follows “{r.otherGame.followsPrimaryName}”</span>
                          )}
                        </div>
                      </div>
                      <Button
                        data-testid={`rel-remove-${r.relationId}`}
                        variant="ghost"
                        size="sm"
                        disabled={relBusy}
                        onClick={() => removeRelation(r.relationId)}
                        className="text-white/40 hover:text-red-400 h-7 px-2"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add a relation: pick the relationship, then click a game */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-white/50 text-xs shrink-0">Relationship:</span>
                <select
                  data-testid="rel-kind-select"
                  value={relKind}
                  onChange={e => setRelKind(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/10 rounded px-2 h-8 text-white text-xs focus:outline-none focus:border-fuchsia-500/50"
                >
                  {REL_KINDS.map(k => (
                    <option key={k.value} value={k.value} className="bg-[#0f0f0f]">{k.label}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="rel-search"
                  placeholder="Search a game to relate..."
                  value={relSearchQ}
                  onChange={e => setRelSearchQ(e.target.value)}
                  className="pl-8 bg-white/5 border-white/10 text-white text-sm"
                />
                {relSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" />}
              </div>
              {relResults.length > 0 && (
                <div className="space-y-1.5 mt-2 max-h-64 overflow-y-auto">
                  {relResults.map(g => {
                    const isGroup = g.versionCount > 1;
                    const isExp = relExpanded === g.versionGroupId;
                    return (
                    <div key={g.id} className="rounded bg-white/[0.03] border border-white/5">
                      <div className="flex items-start gap-2 p-2">
                        {g.boxArtUrl && <img src={g.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm break-words leading-snug">{g.name}</div>
                          <div className="text-white/40 text-xs break-words mt-0.5">{fmtMeta(g) || "—"}</div>
                          {isGroup && (
                            <button
                              type="button"
                              onClick={() => setRelExpanded(isExp ? null : g.versionGroupId)}
                              className="mt-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25"
                              data-testid={`rel-versions-${g.versionGroupId}`}
                              title="This represents all versions. Expand to link a specific one."
                            >
                              <Layers className="w-2.5 h-2.5" />{g.versionCount} versions
                              {isExp ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                            </button>
                          )}
                        </div>
                        <Button
                          data-testid={`rel-add-${g.id}`}
                          variant="ghost"
                          size="sm"
                          disabled={relBusy}
                          onClick={() => addRelation(g)}
                          className="text-fuchsia-400 hover:text-fuchsia-300 h-7 px-2"
                          title="Link this game (as a group it uses the primary version)"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {isGroup && isExp && (
                        <div className="ml-4 pl-2 border-l border-cyan-500/20 pb-2 space-y-1" data-testid={`rel-members-${g.versionGroupId}`}>
                          <p className="text-[9px] text-white/40 uppercase tracking-wider">Link a specific version instead:</p>
                          {(g.members || []).map(m => (
                            <div key={m.id} className="flex items-center justify-between gap-2 pr-2">
                              <div className="min-w-0">
                                <span className="text-white/80 text-[11px] break-words">{m.name}</span>
                                <span className="text-white/30 text-[10px] ml-1">{fmtMeta(m)}</span>
                              </div>
                              <Button
                                data-testid={`rel-add-member-${m.id}`}
                                variant="ghost" size="sm" disabled={relBusy}
                                onClick={() => addRelation(m)}
                                className="text-fuchsia-400 hover:text-fuchsia-300 h-6 px-1.5"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white">Cancel</Button>
              <Button
                data-testid="vl-save"
                onClick={handleSave}
                disabled={saving}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Links
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
